import { useEffect, useRef } from 'react';
import { Check, Coffee, Maximize2, Pause, Play, Square } from 'lucide-react';
import { Button, cn } from '@ai-tutor/ui';
import type { FocusViewModel } from './focusViewModel';

export interface StageActionHandlers {
  onStart: () => void;
  onPause: () => void;
  onResume: () => void;
  onFinish: () => void;
  onNextBlock: () => void;
  onEndSession: () => void;
}

/** A key, shown where its action is. */
export function Kbd({ children }: { children: string }) {
  return (
    <kbd className='rounded border border-border bg-background/70 px-1.5 py-0.5 font-mono text-xs text-foreground'>
      {children}
    </kbd>
  );
}

const nextRound = (view: FocusViewModel) =>
  view.round ? view.round.current + (view.phase === 'break' ? 1 : 0) : 0;

/** The one action that matters now, and the way out of the session. */
export function StageActions({
  view,
  done,
  ...h
}: StageActionHandlers & { view: FocusViewModel; done: boolean }) {
  if (done) return null;
  if (view.phase === 'idle' && !view.between) {
    return (
      // On a phone the set-up comes first, so Start rides along the bottom.
      <div className='fixed inset-x-4 bottom-4 z-30 sm:static'>
        <Button
          size='lg'
          onClick={h.onStart}
          className='w-full shadow-elevation-2 max-sm:h-11 sm:w-auto sm:min-w-[12rem] sm:shadow-none'
        >
          <Play className='mr-2 h-4 w-4' aria-hidden='true' />
          Start focus
        </Button>
      </div>
    );
  }
  if (view.phase === 'break' || view.between) {
    const n = nextRound(view);
    return (
      <div className='flex flex-col items-center gap-3'>
        <p className='flex items-center gap-2 text-sm text-muted-foreground'>
          {view.between ? (
            "Break's over. Ready when you are."
          ) : (
            <>
              <Coffee className='h-4 w-4 shrink-0' aria-hidden='true' />
              {view.breakKind === 'long'
                ? "You've earned it. Step away from the screen."
                : 'Rest is part of learning. Skip the phone.'}
            </>
          )}
        </p>
        <div className='flex flex-wrap justify-center gap-2'>
          {/* During a rest, going back is offered, not pressed on you. */}
          <Button
            size='lg'
            className='max-sm:h-11'
            variant={view.between ? 'default' : 'outline'}
            onClick={h.onNextBlock}
          >
            <Play className='mr-2 h-4 w-4' aria-hidden='true' />
            {n
              ? `Start round ${n}${view.between ? '' : ' now'}`
              : 'Back to focus'}
          </Button>
          <Button
            size='lg'
            className='max-sm:h-11'
            variant='ghost'
            onClick={h.onEndSession}
          >
            End session
          </Button>
        </div>
      </div>
    );
  }
  const paused = view.phase === 'paused';
  const flow = view.progress === null;
  return (
    // Two equal halves on a phone, so Pause and End never wrap unevenly.
    <div className='grid w-full max-w-sm grid-cols-2 gap-2 sm:flex sm:w-auto sm:max-w-none sm:justify-center'>
      {paused ? (
        <Button
          size='lg'
          onClick={h.onResume}
          className='max-sm:h-11 sm:min-w-[9rem]'
        >
          <Play className='mr-2 h-4 w-4' aria-hidden='true' />
          Resume
        </Button>
      ) : (
        <Button
          size='lg'
          variant='outline'
          onClick={h.onPause}
          className='max-sm:h-11 sm:min-w-[9rem]'
        >
          <Pause className='mr-2 h-4 w-4' aria-hidden='true' />
          Pause
        </Button>
      )}
      <Button
        size='lg'
        variant={flow ? 'default' : 'outline'}
        onClick={h.onFinish}
        className='max-sm:h-11 sm:min-w-[9rem]'
      >
        {flow ? (
          <Check className='mr-2 h-4 w-4' aria-hidden='true' />
        ) : (
          <Square className='mr-2 h-4 w-4' aria-hidden='true' />
        )}
        {flow ? "I'm done" : 'End round'}
      </Button>
    </div>
  );
}

const SPACE: Partial<Record<FocusViewModel['phase'], string>> = {
  idle: 'to start',
  focus: 'to pause',
  paused: 'to resume',
};

/**
 * One quiet row under the actions: the way into focus mode, and the keys.
 * `focusReturn`: just back from focus mode, the button takes focus again (the
 * layer's own focus return cannot - this button was not mounted meanwhile).
 */
export function StageFooter({
  view,
  done,
  immersive,
  onEnterFocusMode,
  focusReturn,
}: {
  view: FocusViewModel;
  done: boolean;
  immersive?: boolean;
  onEnterFocusMode?: () => void;
  focusReturn?: { pending: boolean; done: () => void };
}) {
  const ref = useRef<HTMLButtonElement>(null);
  const showButton = Boolean(
    onEnterFocusMode &&
      !immersive &&
      !done &&
      (view.phase !== 'idle' || view.between)
  );
  useEffect(() => {
    if (!showButton || !focusReturn?.pending) return;
    ref.current?.focus();
    focusReturn.done();
  }, [showButton, focusReturn]);
  const space = done ? null : view.between ? 'to start' : SPACE[view.phase];
  if (!showButton && !space && !immersive) return null;
  return (
    <div className='flex flex-wrap items-center justify-center gap-x-4 gap-y-2'>
      {showButton && (
        <Button
          ref={ref}
          variant='ghost'
          size='sm'
          onClick={onEnterFocusMode}
          className='text-muted-foreground max-sm:h-11'
        >
          <Maximize2 className='mr-2 h-4 w-4' aria-hidden='true' />
          Focus mode
          <span
            className='ml-2 hidden [@media(pointer:fine)]:inline'
            aria-hidden='true'
          >
            <Kbd>F</Kbd>
          </span>
        </Button>
      )}
      <p
        className={cn(
          'hidden items-center gap-4 text-xs text-muted-foreground sm:[@media(pointer:fine)]:flex',
          view.phase === 'idle' &&
            !view.between &&
            'sm:[@media(max-height:760px)]:hidden'
        )}
      >
        {space && (
          <span>
            <Kbd>Space</Kbd> {space}
          </span>
        )}
        {immersive && (
          <span>
            <Kbd>Esc</Kbd> to leave focus mode
          </span>
        )}
      </p>
    </div>
  );
}
